import { useEffect, useState } from "react";
import { toString, uniq } from "lodash";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { useGetSearchQualityItemsLazyQuery } from "@bonliva-crm/graphql";
import { SearchQualityCheckStatus } from "@bonliva-crm/shared";
import { ErrorCard } from "@common";
import { QualityCheckFilterData } from "../utils";
import { QualityCheckNameSelector } from "./quality-check-name-selector";
import { QualityCheckStatusSelector } from "./quality-check-status-selector";
import { DatePicker } from "@bonliva-crm/ui";
import moment from "moment";

type Props = {
  open: boolean;
  initialValue: QualityCheckFilterData;
  onChange: (value: QualityCheckFilterData | null) => void;
  onClose: () => void;
};

export function QualityCheckFilterModal(props: Props) {
  const [value, setValue] = useState(props.initialValue);

  const [loadItems, { data, loading, error }] = useGetSearchQualityItemsLazyQuery();

  useEffect(() => {
    setValue(props.initialValue);
  }, [props.open, props.initialValue]);

  useEffect(() => {
    if (props.open) {
      loadItems();
    }
  }, [props.open, loadItems]);

  const handleClear = () => props.onChange(null);

  const handleSave = () => props.onChange(value);

  const qualityItems = data?.getConsultantSearchQualityItems;

  const statusOptions =
    value?.qualityCheckName && qualityItems
      ? Array.from(
          qualityItems.find(({ name }) => name === value.qualityCheckName)?.statuses ?? []
        )
      : [];

  if (value?.qualityCheckName === "Hälsodeklaration") {
    // eslint-disable-next-line
    // @ts-ignore
    statusOptions?.push("MRSA_updateOverdue" as SearchQualityCheckStatus);
  }

  const isReferenceCheck =
    value?.qualityCheckName === "Referenser" ||
    value?.qualityCheckName === "Referenser Nationella";

  return (
    <Dialog open={props.open} maxWidth="md" fullWidth onClose={props.onClose}>
      <DialogTitle>{locale.QualityCheckDocument}</DialogTitle>

      {error && (
        <DialogContent>
          <ErrorCard title={locale.Error} message={toString(error)} />
        </DialogContent>
      )}

      {loading && !data?.getConsultantSearchQualityItems && (
        <DialogContent>
          <Box>
            <CircularProgress />
          </Box>
        </DialogContent>
      )}

      {!loading && data?.getConsultantSearchQualityItems && (
        <DialogContent>
          <Box display="flex">
            <QualityCheckNameSelector
              onChange={(v) => {
                setValue({
                  qualityCheckName: v,
                  qualityCheckStatuses: [],
                  from: null,
                  to: null
                });
              }}
              items={data.getConsultantSearchQualityItems}
              value={value?.qualityCheckName ?? null}
            />

            <Divider
              flexItem
              orientation="vertical"
              style={{ marginLeft: 16, marginRight: 16 }}
            />

            {value?.qualityCheckName && (
              <Box flex="1">
                <>
                  {value?.qualityCheckName && (
                    <QualityCheckStatusSelector
                      onChange={(v) => {
                        setValue({
                          ...value,
                          qualityCheckStatuses: v as SearchQualityCheckStatus[]
                        });
                      }}
                      items={uniq(statusOptions)}
                      value={value.qualityCheckStatuses ?? []}
                      qualityCheckName={value?.qualityCheckName}
                    />
                  )}
                  {value &&
                    value?.qualityCheckStatuses &&
                    value?.qualityCheckStatuses?.length > 0 &&
                    !isReferenceCheck && (
                      <Box
                        style={{ paddingTop: 10, gap: 5, width: 250 }}
                        display={"flex"}
                        flexDirection="column"
                      >
                        <DatePicker
                          fullWidth
                          isClearable
                          value={value?.from ?? null}
                          onChange={(v) => {
                            setValue({ ...value, from: v });
                          }}
                          label={locale.StartDate}
                          DatePickerProps={{
                            maxDate: value?.to
                              ? moment(value?.to).subtract(1, "day").toDate()
                              : moment().subtract(1, "day").toDate(),
                            showWeekNumbers: true,
                            dateFormat: "w | yyyy-MM-dd"
                          }}
                        />
                        <DatePicker
                          fullWidth
                          isClearable
                          value={value?.to ?? null}
                          onChange={(v) => {
                            setValue({ ...value, to: v });
                          }}
                          label={locale.EndDate}
                          DatePickerProps={{
                            maxDate: moment().toDate(),
                            showWeekNumbers: true,
                            dateFormat: "w | yyyy-MM-dd"
                          }}
                        />
                      </Box>
                    )}
                </>
              </Box>
            )}

            {!value?.qualityCheckName && (
              <Box display="flex" alignItems="center" justifyContent="center" flex="1">
                <Typography variant="body1" color="textSecondary">
                  {locale.SelectOneQualityCheckNameFilter}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      )}

      <DialogActions>
        <Button onClick={props.onClose}>{locale.Cancel}</Button>
        <Button onClick={handleClear}>{locale.Clear}</Button>
        <Button color="primary" onClick={handleSave}>
          {locale.OK}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
